import { useQuery } from '@apollo/client';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Cartesian3, Cartographic, HeadingPitchRoll } from 'cesium';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { Role } from '~/apollo/generated/v3/graphql';
import { graphql } from '~/apollo/generated/v4';
import type { VomViewerPageQuery } from '~/apollo/generated/v4/graphql';
import { CesiumAssetState } from '~/apollo/generated/v4/graphql';
import { RoleSecured } from '~/components/auth/RoleSecured';
import { CesiumViewer } from '~/components/cesium/CesiumViewer';
import type { TilesetParams } from '~/components/cesium/cesiumUtils';
import { TerrainProvider } from '~/components/cesium/cesiumUtils';
import { NotFound } from '~/components/common/NotFound';
import { PageHeading } from '~/components/common/PageHeading';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import type { BreadcrumbItem } from '~/components/layout/Breadcrumb';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import {
  outcropRoute,
  regionRoute,
  searchRoute,
  uploadVomUpdateModelDataRoute,
  vomRoute,
} from '~/paths';

const VOM_VIEWER_PAGE_NEW = graphql(`
  query VomViewerPage($id: Int!) {
    virtualOutcropModelList(filter: { id: { eq: $id } }) {
      results {
        id
        name
        cesiumAsset {
          id
          tilesetToken {
            token
          }
          tilesetUrlLocal
          state
          clippingPolygon {
            lat
            lng
          }
          isClipping
          location {
            latitude
            longitude
            height
            heading
            pitch
            roll
          }
          defaultCamera {
            latitude
            longitude
            height
            heading
            pitch
            roll
          }
        }
        outcrop {
          id
          name
          region {
            id
            name
          }
        }
      }
    }
  }
`);

type VomType = NonNullable<
  VomViewerPageQuery['virtualOutcropModelList']
>['results'];
type ResultsType = NonNullable<VomType>[number];
type CAType = ResultsType['cesiumAsset'];

export default function VomViewerRouteWrapper() {
  const params = useParams();
  if (!params.vomId) {
    return <NotFound />;
  }
  const vomId = parseInt(params.vomId);

  return (
    <ApolloProviderV4>
      <InnerPage id={vomId} />
    </ApolloProviderV4>
  );
}

export function InnerPage({ id }: { id: number }) {
  const { data, loading } = useQuery(VOM_VIEWER_PAGE_NEW, {
    variables: {
      id,
    },
  });
  const vom = data?.virtualOutcropModelList?.results?.find(
    v => v.id === String(id),
  );

  if (loading) return <SpinnerPlaceholder />;
  if (!vom) return <NotFound />;
  if (!vom.cesiumAsset) return <NotFound />;

  const cesiumAsset = vom.cesiumAsset;

  return <VomViewerRoute vom={vom} cesiumAsset={cesiumAsset} />;
}

function VomViewerRoute({
  vom,
  cesiumAsset,
}: {
  vom: ResultsType;
  cesiumAsset: NonNullable<CAType>;
}) {
  const [showGlobe, setShowGlobe] = useState(true);
  const [tilesetClipping] = useState(cesiumAsset.isClipping);
  const breadcrumb: BreadcrumbItem[] | undefined = vom?.outcrop
    ? [
        {
          routeId: 'routes/search',
          pathname: searchRoute(),
          title: 'Search Analogues',
        },
        {
          routeId: 'routes/region/$regionId',
          pathname: regionRoute(parseInt(vom.outcrop.region.id)),
          title: vom.outcrop.region.name,
        },
        {
          routeId: 'routes/outcrop/$outcropId',
          pathname: outcropRoute(parseInt(vom.outcrop.id)),
          title: vom.outcrop.name,
        },
        {
          routeId: 'routes/outcrop/$outcropId/virtual-outcrops/$vomId',
          pathname: vomRoute(parseInt(vom.outcrop.id), parseInt(vom.id)),
          title: vom.name,
        },
      ]
    : undefined;

  useBreadcrumb(
    'routes/outcrop/$outcropId/virtual-outcrops/$vomId/viewer',
    'VOM Viewer',
    undefined,
    breadcrumb,
  );

  if (!cesiumAsset.tilesetUrlLocal) return <NotFound />;
  if (!cesiumAsset.tilesetToken?.token) return <NotFound />;

  const ca: TilesetParams = {
    assetToken: cesiumAsset.tilesetToken?.token,
    localPath: cesiumAsset.tilesetUrlLocal,
    transform: { local: true },
  };

  if (cesiumAsset.location) {
    ca.transform = {
      location: Cartographic.fromDegrees(
        cesiumAsset.location.longitude,
        cesiumAsset.location.latitude,
        cesiumAsset.location.height,
      ),
      orientation: new HeadingPitchRoll(
        cesiumAsset.location.heading,
        cesiumAsset.location.pitch,
        cesiumAsset.location.roll,
      ),
    };
  }

  if (cesiumAsset.defaultCamera) {
    ca.defaultCamera = {
      position: Cartesian3.fromDegrees(
        cesiumAsset.defaultCamera.longitude,
        cesiumAsset.defaultCamera.latitude,
        cesiumAsset.defaultCamera.height,
      ),
      orientation: new HeadingPitchRoll(
        cesiumAsset.defaultCamera.heading,
        cesiumAsset.defaultCamera.pitch,
        cesiumAsset.defaultCamera.roll,
      ),
    };
  }

  const setTerrain = () => {
    if (cesiumAsset.state === CesiumAssetState.Complete) {
      return TerrainProvider.World;
    }
    return TerrainProvider.None;
  };

  return (
    <>
      <div className="flex gap-6 justify-between items-baseline">
        <div>
          <PageHeading hasSubtitle className="mt-0">
            VOM Viewer
          </PageHeading>
          <PageHeading.Subtitle>{vom.name}</PageHeading.Subtitle>
        </div>
        <RoleSecured roles={[Role.RoleAdmin]}>
          <div>
            <Link
              to={uploadVomUpdateModelDataRoute(parseInt(vom.id))}
              className="btn btn-ghost btn-sm gap-1"
            >
              <FontAwesomeIcon icon={faPencil} /> Edit
            </Link>
          </div>
        </RoleSecured>
      </div>

      <CesiumViewer
        initialTilesets={[ca]}
        terrainProvider={setTerrain()}
        enableNavigation={true}
        tilesetClipping={tilesetClipping}
        showGlobe={showGlobe}
        clippingPolygon={cesiumAsset.clippingPolygon }
      />
      <div className="float-right	">
        <label className="label">
          <input
            type="checkbox"
            className="toggle toggle-primary m-2"
            checked={showGlobe}
            onChange={e => {
              setShowGlobe(e.target.checked);
            }}
          />
          Show Globe
        </label>
      </div>
    </>
  );
}
